// Location typeahead with a Google Maps fallback.
//
// Drop-in sibling of RelationshipPicker for location FK fields: same props,
// same debounce / click-outside / chip-when-selected behaviour. The dropdown
// shows two groups when open with a query:
//   (a) DB matches from the caller-supplied `searchFn` (identical contract to
//       RelationshipPicker's), and
//   (b) a "From Google Maps" section from the /places autocomplete proxy.
// Clicking a Google row resolves the place (lat/lng + country), infers the
// location type (airport when Google says so, city otherwise) and
// AUTO-CREATES the location via POST /admin/locations — the row lands as
// status 'draft', hidden from the storefront until an operator publishes it.
//
// When the Places proxy errors (e.g. 503 unconfigured), the Google section is
// skipped entirely and the picker degrades to DB-only — no crash.

import { useEffect, useId, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  createAdminLocation,
  fetchPlaceDetails,
  searchPlaces,
  type PlaceSuggestion,
} from '@/lib/admin-api';
import type { RelationshipOption } from './RelationshipPicker';

interface LocationPickerProps {
  label: string;
  value: number | null;
  /** When `value` is set, the picker shows this label as the current selection.
   *  Pass null to fall back to "(id N)" fallback rendering. */
  valueLabel?: string | null;
  valueSub?: string | null;
  onChange: (id: number | null, option?: RelationshipOption) => void;
  searchFn: (q: string, signal?: AbortSignal) => Promise<RelationshipOption[]>;
  placeholder?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  emptyLabel?: string;
}

export function LocationPicker({
  label,
  value,
  valueLabel,
  valueSub,
  onChange,
  searchFn,
  placeholder = 'Type to search…',
  hint,
  required,
  disabled,
  emptyLabel = 'No matches.',
}: LocationPickerProps) {
  const reactId = useId();
  const inputId = `loc-${reactId}`;
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  /** placeId of the Google row currently resolving/creating. */
  const [creatingId, setCreatingId] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);

  // Light debounce so we don't fire a search on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  // A new query invalidates any stale inline error.
  useEffect(() => {
    setCreateError(null);
  }, [debounced]);

  // Click-outside to close the dropdown.
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const results = useQuery({
    queryKey: ['location-picker-db', label, debounced],
    queryFn: ({ signal }) => searchFn(debounced, signal),
    enabled: open && debounced.length > 0,
    staleTime: 30_000,
  });

  // Google Places fallback. On error (e.g. 503 unconfigured) the section is
  // skipped — DB-only fallback, so don't retry-hammer the proxy either.
  const googleResults = useQuery({
    queryKey: ['location-picker-google', debounced],
    queryFn: ({ signal }) => searchPlaces(debounced, signal),
    enabled: open && debounced.length > 0,
    staleTime: 30_000,
    retry: false,
  });

  const isSelected = value != null;

  async function pickGooglePlace(s: PlaceSuggestion) {
    if (creatingId) return; // one create at a time
    setCreatingId(s.id);
    setCreateError(null);
    try {
      const details = await fetchPlaceDetails(s.id);
      if (!details.countryCode) {
        setCreateError("Couldn't resolve country — create it manually under Locations");
        return;
      }
      const type = s.types.includes('airport') ? 'airport' : 'city';
      const created = await createAdminLocation({
        name: s.primaryText,
        type,
        countryCode: details.countryCode,
        latitude: details.latitude,
        longitude: details.longitude,
      });
      onChange(created.id, {
        id: created.id,
        label: created.name,
        sub: `${created.type} · ${created.countryCode}`,
      });
      setQuery('');
      setOpen(false);
    } catch {
      setCreateError("Couldn't create the location — try again or add it under Locations");
    } finally {
      setCreatingId(null);
    }
  }

  const dbRows = results.data ?? [];
  const googleRows = googleResults.data ?? [];
  const showGoogleSection =
    !googleResults.error && (googleResults.isLoading || googleRows.length > 0);

  return (
    <div ref={wrapperRef} className="relative flex flex-col gap-1">
      <label htmlFor={inputId} className="text-[11px] font-medium uppercase tracking-wide text-[color:var(--bo-mute)]">
        {label}
        {required ? <span className="ms-0.5 text-[#9f1239]">*</span> : null}
      </label>

      {isSelected ? (
        <div className="flex items-center justify-between gap-2 rounded-md border border-[color:var(--bo-line)] bg-[color:var(--bo-bg)] px-3 py-2">
          <div className="min-w-0">
            <div className="truncate text-[13px] font-medium text-[color:var(--bo-ink)]">
              {valueLabel ?? `(id ${value})`}
            </div>
            {valueSub ? (
              <div className="truncate text-[11px] text-[color:var(--bo-mute)]">{valueSub}</div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => onChange(null)}
            disabled={disabled}
            className="text-[12px] font-medium text-[#9f1239] hover:underline disabled:opacity-50"
          >
            Clear
          </button>
        </div>
      ) : (
        <input
          id={inputId}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          className="h-10 w-full rounded-md border border-[color:var(--bo-line)] bg-white px-3 text-[13px] text-[color:var(--bo-ink)] focus:border-[color:var(--bo-mute)] focus:outline-none disabled:cursor-not-allowed disabled:bg-[color:var(--bo-bg)]"
        />
      )}

      {hint ? <span className="text-[11px] text-[color:var(--bo-mute)]">{hint}</span> : null}

      {open && !isSelected ? (
        <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-72 overflow-y-auto rounded-md border border-[color:var(--bo-line)] bg-white shadow-lg">
          {debounced.length === 0 ? (
            <div className="px-3 py-2 text-[12px] text-[color:var(--bo-mute)]">
              Start typing to search…
            </div>
          ) : (
            <>
              {createError ? (
                <div className="px-3 py-2 text-[12px] text-[#9f1239]">{createError}</div>
              ) : null}

              {results.isLoading ? (
                <div className="px-3 py-2 text-[12px] text-[color:var(--bo-mute)]">Searching…</div>
              ) : results.error ? (
                <div className="px-3 py-2 text-[12px] text-[#9f1239]">
                  {(results.error as Error).message}
                </div>
              ) : dbRows.length === 0 && !showGoogleSection ? (
                <div className="px-3 py-2 text-[12px] text-[color:var(--bo-mute)]">{emptyLabel}</div>
              ) : (
                <ul className="divide-y divide-[color:var(--bo-line)]">
                  {dbRows.map((opt) => (
                    <li key={opt.id}>
                      <button
                        type="button"
                        onClick={() => {
                          onChange(opt.id, opt);
                          setQuery('');
                          setOpen(false);
                        }}
                        className="block w-full px-3 py-2 text-start text-[13px] text-[color:var(--bo-ink)] hover:bg-[color:var(--bo-bg)]"
                      >
                        <div className="truncate font-medium">{opt.label}</div>
                        {opt.sub ? (
                          <div className="truncate text-[11px] text-[color:var(--bo-mute)]">
                            {opt.sub}
                          </div>
                        ) : null}
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {showGoogleSection ? (
                <div className="border-t border-[color:var(--bo-line)]">
                  <div className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
                    From Google Maps
                  </div>
                  {googleResults.isLoading ? (
                    <div className="px-3 py-2 text-[12px] text-[color:var(--bo-mute)]">
                      Searching Google Maps…
                    </div>
                  ) : (
                    <ul className="divide-y divide-[color:var(--bo-line)]">
                      {googleRows.map((s) => {
                        const busy = creatingId === s.id;
                        return (
                          <li key={s.id}>
                            <button
                              type="button"
                              onClick={() => void pickGooglePlace(s)}
                              disabled={creatingId != null}
                              className="block w-full px-3 py-2 text-start text-[13px] text-[color:var(--bo-ink)] hover:bg-[color:var(--bo-bg)] disabled:cursor-wait disabled:opacity-60"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="min-w-0">
                                  <div className="truncate font-medium">{s.primaryText}</div>
                                  {s.secondaryText ? (
                                    <div className="truncate text-[11px] text-[color:var(--bo-mute)]">
                                      {s.secondaryText}
                                    </div>
                                  ) : null}
                                </div>
                                <span className="shrink-0 text-[10px] text-[color:var(--bo-mute)]">
                                  {busy ? 'Creating…' : 'creates location'}
                                </span>
                              </div>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
